"use client"

import { Facebook, Mail } from "lucide-react"
import { Button } from "@/components/ui/common/Button"
import { Link } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/forms/form/Form"
import FormWrap from "@/components/forms/form/FormWrap"
import { Input } from "@/components/forms/inputs/text/Input"
import { loginService } from "@/api/services/auth/authService"
import displayError from "@/api/errors/errorResponse"
import { useState } from "react"
import useAuthStore from "@/store/userStore"
import skillpassLogo from "@/assets/skillpass_v2.svg"

const formSchema = z.object({
  email: z.string().email(),
  password: z.string()
})

export default function LoginForm() {
  const [loading, setLoading] = useState(false)
  const fetchUserData = useAuthStore((state) => state.fetchUserData)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setLoading(true)
    const res = await loginService(data)
    if(!res.success){
      const error = displayError(res)
      console.error(error)
      setLoading(false)
      return
    }
    await fetchUserData()
    setLoading(false)
  }

  return (
    <div className="min-h-screen flex">
      {/* Lewa strona z logo */}
      <div className="w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full">
          <img src={skillpassLogo} alt="Skillpass Logo" className="w-full h-auto" />
        </div>
      </div>

      {/* Prawa strona z formularzem */}
      <div className="w-1/2 bg-gradient-to-br from-[#1d475f] to-[#2a5f7c] flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-white text-2xl font-semibold mb-2">Witaj!</h1>
            <p className="text-gray-200 text-sm">Zaloguj się, aby kontynuować w SkillPass.</p>
          </div>

          <Form {...form}>
            <FormWrap
              buttonClassName="w-full bg-white hover:bg-gray-100 text-[#1d475f] font-medium"
              onSubmit={form.handleSubmit(onSubmit)}
              submitText="Zaloguj się"
              disabled={loading}
            >
              <div className="flex w-full flex-col gap-5">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Wprowadź email"
                          type="email"
                          {...field}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                        />
                      </FormControl>
                      <FormMessage className="text-red-300" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Hasło</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Wprowadź hasło"
                          type="password"
                          {...field}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                        />
                      </FormControl>
                      <FormMessage className="text-red-300" />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end">
                  <Link to="/forgotpassword" className="text-sm text-white/80 hover:text-white">
                    Zapomniałeś hasła?
                  </Link>
                </div>
              </div>
            </FormWrap>
          </Form>

          <div className="mt-8">
            <div className="text-center text-sm text-white/80 mb-4">Zaloguj się przez</div>
            <div className="flex justify-center space-x-3">
              <Button variant="outline" size="icon" className="rounded-full w-10 h-10 border-white/20 hover:bg-white/10">
                <Facebook className="w-4 h-4 text-white" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full w-10 h-10 border-white/20 hover:bg-white/10">
                <Mail className="w-4 h-4 text-white" />
              </Button>
            </div>
          </div>

          <div className="mt-8 text-center">
            <span className="text-sm text-white/80">Nie masz konta? </span>
            <Link to="/signup" className="text-sm text-white hover:text-white/90 font-medium">
              Zarejestruj się teraz
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
